import {
  DatePicker,
  HiddenInput,
  Input,
  MultipleSelect,
  Select,
  TagInput,
  Textarea,
} from '~components/generic/elements';
import { FieldSettings, FieldValue } from '~extension/web2print/model/FieldSettings';
import { IValueOptionsShape } from '~services/entity-definitions/interface';

type FieldGroupProps = {
  fieldSettings: FieldSettings;
  fieldValue: FieldValue;
  error?: string;
  onValueChanged?: (key: string, value: FieldValue) => void;
};

export const FieldGroup = ({ fieldSettings, fieldValue, onValueChanged, error }: FieldGroupProps) => {
  function field() {
    switch (fieldSettings.type) {
      case 'textarea':
        return (
          <Textarea
            name={fieldSettings.fieldname}
            value={fieldValue !== undefined ? String(fieldValue) : undefined}
            onChange={(value: string) => onValueChanged(fieldSettings.fieldname, value)}
          />
        );
      case 'select':
        return (
          <Select
            name={fieldSettings.fieldname}
            value={fieldValue !== undefined ? String(fieldValue) : undefined}
            options={fieldSettings.options}
            onChange={(value: string | number) => onValueChanged(fieldSettings.fieldname, value)}
            placeholder={'Please choose'}
            fakeLevelOneOptgroup={true}
          />
        );
      case 'multipleselect':
        return (
          <MultipleSelect
            name={fieldSettings.fieldname}
            defaultValue={fieldValue}
            options={getMultipleSelectOptions(fieldSettings.options)}
            onChange={(value: string[] | number[]) => onValueChanged(fieldSettings.fieldname, value as FieldValue)}
            placeholder="Select Language"
          />
        );
      case 'taginput':
        return (
          <TagInput
            name={fieldSettings.fieldname}
            defaultValue={fieldValue}
            onChange={(value: string) => onValueChanged(fieldSettings.fieldname, value)}
          />
        );
      case 'datepicker':
        return (
          <DatePicker
            name={fieldSettings.fieldname}
            defaultValue={fieldValue !== undefined ? String(fieldValue) : undefined}
            onChange={(value: string) => onValueChanged(fieldSettings.fieldname, value)}
            minDate={new Date()}
          />
        );
      case 'hidden':
        return <HiddenInput name={fieldSettings.fieldname} value={fieldValue} />;

      default:
        return (
          <Input
            type="text"
            name={fieldSettings.fieldname}
            value={fieldValue !== undefined ? String(fieldValue) : undefined}
            onChange={(value: string) => onValueChanged(fieldSettings.fieldname, value)}
          />
        );
    }
  }

  function errorMessage() {
    if (error !== null) {
      return <div className="error c-hint--error text-sm">{error}</div>;
    }
    return null;
  }

  // options for mutlipleselect can be a list of {id: <option-label>, value: <option-value>}
  // or just a simple object where keys are option-labels, values are option-values
  function getMultipleSelectOptions(options?: Record<string, any>): IValueOptionsShape {
    if (options) {
      return Object.keys(options).map(key => {
        return { label: options[key].value ?? key, value: options[key].id ?? options[key] };
      });
    }
    return [];
  }

  if (fieldSettings.type === 'hidden') {
    return field();
  }

  return (
    <div className="form-group">
      <div className="p42-label">
        <label>
          {fieldSettings.label}
          {Boolean(fieldSettings.required) && '*'}
        </label>
        <div className="label-info">
          <div className="tooltip-info">
            <h3>{fieldSettings.label}</h3>
            <p>{fieldSettings.tooltip}</p>
          </div>
        </div>
      </div>
      {field()}
      {errorMessage()}{' '}
    </div>
  );
};

export default FieldGroup;
